import {
	useMutation,
	useQueryClient,
	type QueryClient,
} from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import {
	deleteInspection,
	upsertInspection,
} from "../../repositories/InspectionRepository";

// Fire-and-forget: callers reset their form right away instead of waiting for
// every list to refetch.
const invalidateInspectionQueries = (queryClient: QueryClient) => {
	void Promise.all(
		[
			"test-shack-inspections",
			"test-shack-rolling-summary",
			"test-shack-shift-inspections",
			"test-shack-report",
			"test-shack-report-filters",
		].map((key) => queryClient.invalidateQueries({ queryKey: [key] })),
	);
};

// The mutation captures its options when mutate() runs. Reading the callback
// through a ref makes onSuccess see the page state at completion, not at submit.
const useLatest = <T>(value: T) => {
	const ref = useRef(value);
	useEffect(() => {
		ref.current = value;
	});
	return ref;
};

interface UpsertInspectionMutationOptions {
	onSuccess?: () => void;
	onError: (error: Error) => void;
}

export const useUpsertInspectionMutation = ({
	onSuccess,
	onError,
}: UpsertInspectionMutationOptions) => {
	const queryClient = useQueryClient();
	const onSuccessRef = useLatest(onSuccess);
	return useMutation({
		mutationFn: upsertInspection,
		retry: false,
		meta: { successMessage: "Inspection saved" },
		onSuccess: () => {
			invalidateInspectionQueries(queryClient);
			onSuccessRef.current?.();
		},
		onError,
	});
};

interface DeleteInspectionMutationOptions {
	onSuccess?: (profileInspectionId: number) => void;
	onError: (error: Error) => void;
}

export const useDeleteInspectionMutation = ({
	onSuccess,
	onError,
}: DeleteInspectionMutationOptions) => {
	const queryClient = useQueryClient();
	const onSuccessRef = useLatest(onSuccess);
	return useMutation({
		mutationFn: deleteInspection,
		retry: false,
		meta: { successMessage: "Inspection deleted" },
		onSuccess: (_, profileInspectionId) => {
			invalidateInspectionQueries(queryClient);
			onSuccessRef.current?.(profileInspectionId);
		},
		onError,
	});
};
